///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";

import DownloadTemplateModal from "./DownloadTemplateModal";
import UploadLanesModal from "./UploadLanesModal";
import { fetchBidSummary } from "../../../slices/bidSummarySlice";
import { fetchPricingLanes } from "../../../slices/bidPricingSlice";
import messages from "../../../helpers/messages";
import {
  status as bidStatus,
  division as divisionTypes,
  mode
} from "../../../helpers/types";
import getModeForUserRole from "../../../helpers/getModeForUserRole";
import StrategySelectionModal from "./StrategySelectionModal";

function NoPricingLanes({
  division,
  bidId,
  role,
  isLaneImportInProgress,
  isLaneRepriceInProgress
}) {
  const dispatch = useDispatch();
  const { useStrategicCostData } = useSelector(state => state.featureFlags);
  const {
    bidSummary: { summary }
  } = useSelector(state => state);

  const bidModes = summary?.modes?.map(m => m.mode) ?? [];

  const [downloadTemplate, toggleDownloadTemplate] = useState(false);
  const [uploadLanesModal, toggleUploadLanesModal] = useState(false);
  const [strategySelectionModal, toggleStrategySelectionModal] =
    useState(false);

  const closeLaneUploadModal = uploaded => {
    toggleUploadLanesModal(false);
    if (uploaded) {
      dispatch(fetchBidSummary({ bidId, role }));
      dispatch(
        fetchPricingLanes({ bidId, role, isCoyoteBid: summary?.isCoyoteBid })
      );
    }
  };

  const handleImportLanes = () => {
    if (
      division === divisionTypes.coy &&
      userMode === mode.tl &&
      useStrategicCostData
    ) {
      toggleStrategySelectionModal(true);
    } else {
      toggleUploadLanesModal(true);
    }
  };

  const showLaneImportModal = () => {
    toggleStrategySelectionModal(false);
    toggleUploadLanesModal(true);
  };

  const userMode = getModeForUserRole(role);

  return (
    <Fragment>
      {downloadTemplate && (
        <DownloadTemplateModal
          handleClose={() => toggleDownloadTemplate(false)}
          isCoyoteBid={summary?.isCoyoteBid}
          bidModes={bidModes}
        />
      )}
      {uploadLanesModal && (
        <UploadLanesModal
          role={role}
          bidId={bidId}
          handleClose={closeLaneUploadModal}
          division={division}
          fetchAfterUpload={false}
          isLaneImport={true}
        />
      )}
      {strategySelectionModal && (
        <StrategySelectionModal
          open={strategySelectionModal}
          handleClose={() => toggleStrategySelectionModal(false)}
          showLaneImportModal={showLaneImportModal}
          bidId={bidId}
        />
      )}

      <div
        data-testid="no-pricing-lanes"
        className="flex justify-center text-center items-center one-whole"
        style={{ height: window.screen.availHeight - 120 }}
      >
        <div style={{ width: "50%", maxWidth: "500px" }}>
          <p className="mb-32 no-lanes-title">
            {messages.noLanesAvailable.defaultMessage}
          </p>
          <p className="mb-36 no-lanes-description">
            {messages.importYourLanesToBeginPricing.defaultMessage}
          </p>
          <button
            onClick={handleImportLanes}
            className="primary-button px-32"
            data-testid="import-lanes"
            disabled={
              (summary &&
                summary.modes.some(
                  x => x.mode === userMode && x.status === bidStatus.closed
                )) ||
              isLaneImportInProgress ||
              isLaneRepriceInProgress
            }
          >
            <b>{messages.importLanes.defaultMessage}</b>
          </button>
          <br />
          <button
            onClick={() => toggleDownloadTemplate(true)}
            className="inline-block text-button mt-16 px-32"
          >
            <b>{messages.downloadTemplate.defaultMessage}</b>
          </button>
        </div>
      </div>
    </Fragment>
  );
}

export default NoPricingLanes;
